import {
 GridCallbackDetails,
 GridFilterItem,
 GridFilterModel,
 GridInitialState,
 GridPaginationModel,
 GridSortModel,
} from '@mui/x-data-grid-premium';
import { Dispatch, SetStateAction } from 'react';

import {
 getConfigStorageItem,
 setConfigStorageItem,
} from '../../../hooks/useConfigStorage';
import { PageNameType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { StructureType } from '../../../types/types';
import { findStructure, isJson } from '../../../utils/dataTypes';
import { isWeightModelField } from '@/utils/structureUtils';
import { isEmptyFilterValue, normalizeGridItem } from './filterModel';

const getTableStateStorageKey = (pageName: PageNameType) =>
 `table_state_${pageName}`;

export const readStoredTableStateRaw = (
 pageName: PageNameType,
): Record<string, unknown> => {
 const stored = getConfigStorageItem(getTableStateStorageKey(pageName));
 if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
  return {};
 }
 return stored as Record<string, unknown>;
};

export const writeStoredTableStateRaw = (
 pageName: PageNameType,
 state: object,
): void => {
 setConfigStorageItem({
  key: getTableStateStorageKey(pageName),
  value: state,
 });
};

export const getDefaultTableInitialState = (): GridInitialState => ({
 pinnedColumns: { right: ['actions'] },
});

export const readStoredInitialState = (
 pageName: PageNameType,
): GridInitialState => {
 const stored = readStoredTableStateRaw(pageName);
 const { sorting: _sorting, ...rest } = stored;
 return {
  ...getDefaultTableInitialState(),
  ...(rest as GridInitialState),
 };
};

export const readStoredSortModel = (pageName: PageNameType): GridSortModel => {
 const stored = readStoredTableStateRaw(pageName);
 const sorting = stored.sorting as { sortModel?: GridSortModel } | undefined;
 return sorting?.sortModel || [];
};

export const readStoredFilterModel = (
 pageName: PageNameType,
): GridFilterModel => {
 const stored = readStoredTableStateRaw(pageName);
 const filterState = stored.filter as
  | { filterModel?: GridFilterModel }
  | undefined;
 return filterState?.filterModel || { items: [] };
};

export const readStoredPaginationModel = (
 pageName: PageNameType,
 defaultPaginationModel: GridPaginationModel,
): GridPaginationModel => {
 const stored = readStoredTableStateRaw(pageName);
 const pagination = stored.pagination as
  | { paginationModel?: GridPaginationModel }
  | undefined;
 return pagination?.paginationModel || defaultPaginationModel;
};

export const persistTableGridState = (
 pageName: PageNameType,
 {
  sortModel,
  filter,
  paginationModel,
 }: {
  sortModel: GridSortModel;
  filter: GridFilterModel | undefined;
  paginationModel: GridPaginationModel;
 },
): void => {
 const stored = readStoredTableStateRaw(pageName);
 writeStoredTableStateRaw(pageName, {
  ...stored,
  sorting: { sortModel },
  filter: { filterModel: filter ?? { items: [] } },
  pagination: { paginationModel },
 });
};

export const filterMap = (
 filters: GridFilterItem[] | undefined,
): GridFilterModel => ({
 items:
  typeof filters !== 'undefined' && Array.isArray(filters)
   ? filters.map((item: GridFilterItem, i: number) => ({
      ...item,
      id: i,
      operatorValue: item?.operator || 'equals',
     }))
   : [],
});

export const parseToInt = (param: string | string[]): number => {
 if (Array.isArray(param)) {
  return parseInt(param[0], 10) || 0;
 }
 return parseInt(param, 10) || 0;
};

export const decodeURI = (param: string | string[]): string | string[] => {
 if (Array.isArray(param)) {
  return param.map((item) => decodeURIComponent(item) || '');
 }
 return decodeURIComponent(param) || '';
};

export const lowercaseKeys = (
 obj: Record<string, unknown> | Array<Record<string, unknown>>,
): unknown => {
 if (Array.isArray(obj)) {
  return obj.map(lowercaseKeys);
 }
 if (obj && typeof obj === 'object') {
  const newObj: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
   newObj[key.toLowerCase()] = lowercaseKeys(
    obj[key] as Record<string, unknown>,
   );
  }
  return newObj;
 }
 return obj;
};

interface RowWithOptionalId {
 id?: string | number;
 no?: string | number;
 [key: string]: unknown;
}

const isValidRowKey = (value: unknown): value is string | number =>
 (typeof value === 'string' && value.length > 0) || typeof value === 'number';

export const constructRows = (
 rows: ReadonlyArray<RowWithOptionalId> | undefined,
): Array<RowWithOptionalId & { rowIndex: number }> => {
 if (!Array.isArray(rows)) return [];

 const firstRow = rows[0] as RowWithOptionalId | undefined;
 const hasUniqueId = isValidRowKey(firstRow?.id) || isValidRowKey(firstRow?.no);

 return rows.map((row: RowWithOptionalId, rowIndex: number) => {
  if (!hasUniqueId) {
   return { ...row, id: rowIndex, rowIndex };
  }
  const resolvedId = isValidRowKey(row.id)
   ? row.id
   : isValidRowKey(row.no)
   ? row.no
   : rowIndex;
  return { ...row, id: resolvedId, rowIndex };
 });
};

const OPERATOR_MAPPINGS: Record<string, string> = {
 contains: 'contains',
 doesNotContain: 'doesnotcontain',
 equals: 'eq',
 doesNotEqual: 'neq',
 startsWith: 'startswith',
 endsWith: 'endswith',
 isEmpty: 'eq',
 isNotEmpty: 'neq',
 '=': 'eq',
 '!=': 'neq',
 '>': 'gt',
 '>=': 'gte',
 '<': 'lt',
 '<=': 'lte',
 is: 'eq',
 not: 'neq',
 after: 'gt',
 before: 'lt',
 onOrAfter: 'gte',
 onOrBefore: 'lte',
 isAnyOf: 'contains',
 modelEqual: 'eq',
 modelNotEqual: 'neq',
};

export const operatorMap = (operator: string): string =>
 OPERATOR_MAPPINGS[operator] || operator;

export interface FilterWithLogicType {
 field: string;
 operator: string;
 value: unknown;
 logic?: 'Or' | 'And';
}

const addLogicToFilters = ({
 filters,
 structure,
}: {
 filters: FilterWithLogicType[];
 structure: StructureType[];
}): FilterWithLogicType[] => {
 if (filters.length <= 1) return filters;

 let result: FilterWithLogicType[] = [];

 const grouped = filters.reduce<Record<string, FilterWithLogicType[]>>(
  (acc, condition) => {
   if (!acc[condition.field]) {
    acc[condition.field] = [];
   }
   acc[condition.field].push(condition);
   return acc;
  },
  {},
 );

 let groupIndex = 0;
 Object.entries(grouped).forEach(([key, groupFilters]) => {
  const filterStructure = findStructure(structure, key);

  if (groupFilters.length > 1) {
   result = [
    ...result,
    ...groupFilters.map((val, filterIndex) => ({
     ...val,
     ...(filterStructure?.Type === 'datetime' && {
      operator: filterIndex === 0 ? 'gte' : 'lte',
     }),
     logic:
      filterStructure?.Type === 'datetime' ? ('And' as const) : ('Or' as const),
    })),
   ];
  } else {
   if (result[groupIndex]) {
    result[groupIndex].logic = 'And';
   }
   result.push({ ...groupFilters[0], logic: 'And' });
  }

  groupIndex += 1;
 });

 if (result.length > 0) {
  delete result[result.length - 1].logic;
 }
 return result;
};

const SORT_ARRAY_FIELD_OVERRIDES: Record<string, string> = {
 ResultProducts: 'Product.Name',
};

const hasInnerStructureField = (
 structure: StructureType,
 path: string,
): boolean => {
 const parts = path.split('.');
 let current: StructureType | null = structure;

 for (const part of parts) {
  const innerField: StructureType | undefined = current?.InnerStructure?.find(
   (s) => s.Name === part,
  );
  if (!innerField) return false;
  current = innerField;
 }

 return true;
};

const resolveSortSubField = (
 field: string,
 sortStructure: StructureType,
): string | null => {
 const override = SORT_ARRAY_FIELD_OVERRIDES[field];
 if (override && hasInnerStructureField(sortStructure, override)) {
  return override;
 }

 if (isJson(sortStructure.ForeignKeyDefaultFilter)) {
  const filter = JSON.parse(sortStructure.ForeignKeyDefaultFilter) as {
   fields: string;
  };
  const candidate = filter.fields.split(',')[1]?.trim();
  if (candidate && hasInnerStructureField(sortStructure, candidate)) {
   return candidate;
  }
 }

 if (hasInnerStructureField(sortStructure, 'Name')) {
  return 'Name';
 }
 if (hasInnerStructureField(sortStructure, 'Id')) {
  return 'Id';
 }

 return null;
};

const getColumnName = (field: string, sortStructure: StructureType): string => {
 if (!sortStructure) return field;

 const subField = resolveSortSubField(field, sortStructure);
 if (subField) {
  return `${field}.${subField}`;
 }

 return field;
};

export const prepareSort = (
 sortModel: GridSortModel,
 structure: StructureType[],
): Array<{ field: string; dir: string }> | null => {
 if (!sortModel.length) return null;

 return sortModel.map((sortItem) => {
  const sortStructure = findStructure(structure, sortItem.field);
  const colName = getColumnName(sortItem.field, sortStructure);
  return {
   field: colName,
   dir: sortItem.sort || 'asc',
  };
 });
};

export const handleFilterModelChange = (
 model: GridFilterModel,
 details: GridCallbackDetails<'filter'>,
 setFilter: Dispatch<SetStateAction<GridFilterModel>>,
) => {
 if (typeof details.reason === 'undefined') return;

 if (model.items.length === 0) {
  setFilter(model);
  return;
 }

 setFilter({ items: model.items.map(normalizeGridItem) });
};

export const prepareFilters = (
 filters: GridFilterItem[],
 structure: StructureType[],
) => {
 if (!filters.length || !structure.length) return null;

 const filtersMap: FilterWithLogicType[] = [];

 filters.forEach((filter: GridFilterItem) => {
  // Skip blank items (e.g. an untouched multi-select left as `[]`) so they
  // never reach the API as an empty `Field.Id` filter.
  if (isEmptyFilterValue(filter?.value)) return;

  const currentStructure = findStructure(structure, filter.field);
  const isStringField = currentStructure?.Type === 'string';
  const incomingOperator = filter.operator;
  const resolvedOperator =
   isStringField && (!incomingOperator || incomingOperator === 'equals')
    ? 'contains'
    : incomingOperator || (isStringField ? 'contains' : 'equals');
  const mappedOperator = operatorMap(resolvedOperator);

  const filterValue = filter?.value;
  const hasWeight =
   filterValue !== null &&
   typeof filterValue === 'object' &&
   'Weight' in filterValue &&
   filterValue.Weight !== undefined;
  const hasId =
   filterValue !== null &&
   typeof filterValue === 'object' &&
   'Id' in filterValue &&
   filterValue.Id !== undefined;

  if (isWeightModelField(currentStructure)) {
   filtersMap.push({
    field: `${filter.field}.Weight`,
    operator: mappedOperator,
    value: hasWeight ? filterValue.Weight : filterValue,
   });
  } else if (['model', 'array', 'enum'].includes(currentStructure?.Type)) {
   filtersMap.push({
    field:
     currentStructure?.Type === 'enum' ? filter.field : `${filter.field}.Id`,
    operator: 'eq',
    value: hasId ? filterValue.Id : filterValue,
   });
  } else {
   filtersMap.push({
    field: hasId ? `${filter.field}.Id` : filter.field,
    operator: mappedOperator,
    value: hasId ? filterValue.Id : filterValue,
   });
  }
 });

 if (filtersMap.length > 0) {
  return {
   filters: addLogicToFilters({ filters: filtersMap, structure }),
  };
 }
 return null;
};

